import { ConflictError, FromBody, HttpStatusCodes, ForbiddenError, Result, NotFoundError, Inject } from '@resourceful-hono/core';
import { BaseResource } from '../BaseResource.ts';
import { POST_SCHEMA, PATCH_SCHEMA } from './schema.ts';
import type { z } from 'zod';
import { User } from '../../../entities/User.ts';
import { UserAccount } from '../../../entities/Account/UserAccount.ts';
import { Protected } from '../common/decorators.ts';
import { Permissions } from '../../../services/OAuth/enums.ts';
import { Game } from '../../../entities/Game.ts';
import { match, P } from 'ts-pattern';
import { UnprocessableContentError } from '../common/errors.ts';
import { PLAYER_PRIVILEGES } from './constants.ts';
import { uniqueFailed } from '../../../entities/common/utils.ts';
import { TransactionsResource } from '../Transactions/index.ts';
import { DataSourceTransactionScope } from '../../../common/datasource-transaction-scope.ts';
import { EventService } from '../../../services/Events.ts';
import { GameEvent } from '../common/events.ts';
import { settle } from '@monocle-money/utils';

export class UserResource extends BaseResource {
  @Inject()
  declare private readonly gameEvents: EventService;

  @Protected([Permissions.GetUser])
  public async GET() {
    const user = await this.user;
    return Result(HttpStatusCodes.Ok, user);
  }

  public async POST(@FromBody(POST_SCHEMA) body: z.infer<typeof POST_SCHEMA>) {
    const user = new User();
    user.email = body.email;
    user.displayName = body.displayName;
    user.privileges = PLAYER_PRIVILEGES;
    const account = new UserAccount();
    account.type = 'user';
    user.account = account;
    
    const result = await settle(user.save());
    return match(result)
      .with({ status: 'fulfilled' }, () => {
        return Result(HttpStatusCodes.Created);
      })
      .with({ status: 'rejected' }, ({ reason }) => {
        match(reason)
          .with(P.instanceOf(Error).and(P.when(uniqueFailed)), () => {
            throw new ConflictError();
          })
          .with(P.any, () => {throw reason;})
          .exhaustive();
      })
      .exhaustive();
  }

  @Protected([Permissions.PatchUser])
  public async PATCH(
    @FromBody(PATCH_SCHEMA) body: z.infer<typeof PATCH_SCHEMA>
  ) {
    const user = await this.user;
    const { displayName = user.displayName, gameId } = body;
    user.displayName = displayName;
    return match(gameId)
      .with(P.nullish, async (gameId) => {
        if (gameId === null) {
          const game = user.game;
          user.game = null;
          
          if (game?.id) {
            // publish to client
            this.gameEvents.dispatchEvent(
              new GameEvent({
                gameId: game.id,
                target: ['game'],
              })
            );
          }
        }
        await user.save();
        return Result(HttpStatusCodes.Ok);
      })
      .with(P.number, async (gameId) => {
        const game = await Game.findBy({ id: gameId });
        if (!game)
          throw new UnprocessableContentError();

        await using scope = await DataSourceTransactionScope.create();
        user.game = { id: gameId } as Game;
        await scope.queryRunner.manager.save([user]);
        const account = await UserAccount.findOne({
          where: {
            owner: {
              id: user.id,
            },
          },
        });
        if (!account)
          throw new UnprocessableContentError('You need an account');
        user.account = account;

        await TransactionsResource.transferStartingCapital(user);

        scope.complete();

        // publish to client
        this.gameEvents.dispatchEvent(
          new GameEvent({
            gameId: gameId,
            target: ['game'],
          })
        );
        return Result(HttpStatusCodes.Ok);
      })
      .exhaustive();
  }
}